// src/components/informatie/ArticleList.tsx
// Artikel overzicht per categorie
// WFT Study Buddy - Content Management System
// MODERN GLASSMORPHISM DESIGN

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  getPublishedArticles,
  Article,
  ArticleCategory,
  truncateText
} from '../../services/contentService';

// Category icons
const CategoryIcons: Record<string, React.ReactNode> = {
  examens: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
    </svg>
  ),
  begrippen: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
  ),
  studietips: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
    </svg>
  ),
  statistieken: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  ),
};

// Category labels
const CategoryLabels: Record<string, string> = {
  examens: 'Examens',
  begrippen: 'Begrippen',
  studietips: 'Studietips',
  statistieken: 'Statistieken',
};

// Category gradient colors
const CategoryGradients: Record<string, string> = {
  examens: 'from-blue-500 to-indigo-600',
  begrippen: 'from-emerald-500 to-teal-600',
  studietips: 'from-amber-500 to-orange-600',
  statistieken: 'from-purple-500 to-pink-600',
};

// Category descriptions for SEO
const categoryDescriptions: Record<string, string> = {
  examens: 'Alles wat je moet weten over WFT examens: scoringsregels, procedures, tips en meer.',
  begrippen: 'WFT begrippen uitgelegd in begrijpelijke taal. Leer de belangrijkste termen kennen.',
  studietips: 'Handige studietips en strategieën om je WFT examen te halen.',
  statistieken: 'Slagingspercentages en statistieken van WFT examens.'
};

const ArticleList: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const ITEMS_PER_PAGE = 12;

  // Track scroll for header styling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    loadArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, page]);

  const loadArticles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getPublishedArticles({
        category: category as ArticleCategory,
        page,
        limit: ITEMS_PER_PAGE
      });
      setArticles(response.articles);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (err: any) {
      setError(err.message || 'Fout bij laden artikelen');
    } finally {
      setLoading(false);
    }
  };

  const categoryLabel = CategoryLabels[category || ''] || category;
  const categoryIcon = CategoryIcons[category || ''] || CategoryIcons.examens;
  const categoryGradient = CategoryGradients[category || ''] || 'from-blue-500 to-indigo-600';

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{categoryLabel} | WFT Study Buddy</title>
        <meta name="description" content={categoryDescriptions[category || ''] || 'WFT examen informatie en studietips.'} />
        <link rel="canonical" href={`https://wft-study-buddy.nl/informatie/${category}`} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 relative">
        {/* Decorative Background Blobs */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" />
          <div className="absolute top-40 right-10 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute bottom-40 left-1/3 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-15" />
        </div>

        {/* Main Header */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white/90 backdrop-blur-lg shadow-lg py-3' : 'bg-white/60 backdrop-blur-sm py-4'
        }`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center">
              {/* Logo */}
              <Link to="/" className="flex items-center space-x-2 group">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center transform group-hover:scale-105 transition-transform">
                  <span className="text-white font-bold text-xl">W</span>
                </div>
                <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  WFT Study Buddy
                </span>
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center space-x-6">
                <Link to="/#features" className="text-gray-600 hover:text-blue-600 transition">Features</Link>
                <Link to="/#how-it-works" className="text-gray-600 hover:text-blue-600 transition">Hoe werkt het?</Link>
                <Link to="/#pricing" className="text-gray-600 hover:text-blue-600 transition">Prijzen</Link>
                <Link to="/informatie" className="text-blue-600 font-medium">Informatie</Link>
                <Link to="/login" className="text-gray-600 hover:text-blue-600 transition">Inloggen</Link>
                <Link 
                  to="/register" 
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-2 rounded-lg hover:shadow-lg transform hover:scale-105 transition-all"
                >
                  Registreren
                </Link>
              </div>

              {/* Mobile Menu Button */}
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden text-gray-600 hover:text-blue-600 transition-colors p-2"
                aria-label="Menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden bg-white/95 backdrop-blur-lg border-t border-gray-100 shadow-lg">
              <div className="px-4 py-4 space-y-3">
                <Link to="/" className="block py-2 text-gray-600 hover:text-blue-600">Home</Link>
                <Link to="/#features" className="block py-2 text-gray-600 hover:text-blue-600">Features</Link>
                <Link to="/#pricing" className="block py-2 text-gray-600 hover:text-blue-600">Prijzen</Link>
                <Link to="/informatie" className="block py-2 text-blue-600 font-medium">Informatie</Link>
                <hr className="border-gray-200" />
                <Link to="/login" className="block py-2 text-gray-600 hover:text-blue-600">Inloggen</Link>
                <Link 
                  to="/register" 
                  className="block w-full text-center bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-2.5 rounded-lg"
                >
                  Registreren
                </Link>
              </div>
            </div>
          )}
        </nav>

        {/* Page Header */}
        <section className="relative z-10 pt-24 sm:pt-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm mb-6">
              <Link to="/" className="text-gray-500 hover:text-blue-600 transition">Home</Link>
              <span className="text-gray-300">/</span>
              <Link to="/informatie" className="text-gray-500 hover:text-blue-600 transition">Informatie</Link>
              <span className="text-gray-300">/</span>
              <span className="text-gray-700 font-medium">{categoryLabel}</span>
            </nav>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
              {/* Category Icon */}
              <div className={`w-16 h-16 bg-gradient-to-br ${categoryGradient} rounded-2xl flex items-center justify-center text-white shadow-lg`}>
                {categoryIcon}
              </div>
              
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                  {categoryLabel}
                </h1>
                <p className="text-lg text-gray-600">
                  {categoryDescriptions[category || ''] || 'Bekijk onze artikelen.'}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Loading */}
          {loading && articles.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center animate-pulse">
                <span className="text-white font-bold text-2xl">W</span>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="text-center py-12 bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50">
              <p className="text-red-500 mb-4">{error}</p>
              <button 
                onClick={loadArticles} 
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all"
              >
                Opnieuw proberen
              </button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && articles.length === 0 && (
            <div className="text-center py-16 bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50">
              <div className={`w-20 h-20 bg-gradient-to-br ${categoryGradient} rounded-2xl flex items-center justify-center mx-auto mb-6 text-white shadow-lg`}>
                {categoryIcon}
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Nog geen artikelen
              </h2>
              <p className="text-gray-500 mb-6">
                Er zijn nog geen artikelen in deze categorie.
              </p>
              <Link
                to="/informatie"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transform hover:scale-105 transition-all"
              >
                ← Bekijk andere categorieën
              </Link>
            </div>
          )}

          {/* Articles Grid */}
          {articles.length > 0 && (
            <>
              <div className="flex items-center justify-between mb-6">
                <span className="text-sm text-gray-600 bg-white/60 backdrop-blur-sm px-4 py-2 rounded-full">
                  {total} artikel{total !== 1 ? 'en' : ''} gevonden
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/informatie/${article.category}/${article.slug}`}
                    className="group bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 overflow-hidden hover:shadow-xl hover:bg-white/90 transition-all duration-300 transform hover:scale-[1.02]"
                  >
                    <div className="p-6">
                      {/* Category & Read Time */}
                      <div className="flex items-center justify-between mb-3">
                        <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg text-xs font-medium bg-gradient-to-r ${CategoryGradients[article.category] || 'from-blue-500 to-indigo-600'} text-white`}>
                          <span className="w-3.5 h-3.5">
                            {CategoryIcons[article.category]}
                          </span>
                          {CategoryLabels[article.category] || article.category}
                        </span>
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {article.readTimeMinutes} min
                        </span>
                      </div>

                      {/* Title */}
                      <h2 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                        {article.title || article.h1}
                      </h2>

                      {/* Description */}
                      <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                        {truncateText(article.metaDescription || article.intro, 120)}
                      </p>

                      {/* Footer */}
                      <div className="flex items-center justify-end pt-4 border-t border-gray-100">
                        <span className="text-sm font-medium text-blue-600 group-hover:translate-x-1 transition-transform flex items-center gap-1">
                          Lees meer
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-12">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white/70 backdrop-blur-sm border border-white/50 rounded-xl hover:bg-white hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    ← Vorige
                  </button>
                  <span className="px-4 py-2 text-sm text-gray-600 bg-white/60 backdrop-blur-sm rounded-full">
                    Pagina {page} van {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white/70 backdrop-blur-sm border border-white/50 rounded-xl hover:bg-white hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Volgende →
                  </button>
                </div>
              )}
            </>
          )}
        </main>

        {/* CTA Section */}
        <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-3xl p-8 sm:p-12 text-center shadow-2xl relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                Klaar om te oefenen?
              </h2>
              <p className="text-blue-100 mb-8 max-w-lg mx-auto">
                Start vandaag nog met oefenvragen en verhoog je slagingskans.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/app"
                  className="px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold hover:bg-blue-50 hover:shadow-lg transform hover:scale-105 transition-all"
                >
                  Start gratis oefenen
                </Link>
                <Link
                  to="/informatie"
                  className="px-8 py-4 bg-white/10 text-white border border-white/30 rounded-xl font-semibold hover:bg-white/20 transition-all"
                >
                  Meer artikelen
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="relative z-10 border-t border-white/30 bg-white/60 backdrop-blur-sm mt-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Brand */}
              <div className="col-span-1 md:col-span-2">
                <Link to="/" className="flex items-center space-x-2 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-xl">W</span>
                  </div>
                  <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    WFT Study Buddy
                  </span>
                </Link>
                <p className="text-gray-600 text-sm max-w-md">
                  Bereid je optimaal voor op je WFT examen met AI-gegenereerde oefenvragen en complete samenvattingen.
                </p>
              </div>

              {/* Links */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4">Navigatie</h4>
                <nav className="space-y-2">
                  <Link to="/informatie" className="block text-sm text-gray-600 hover:text-blue-600 transition">Informatie</Link>
                  <Link to="/app" className="block text-sm text-gray-600 hover:text-blue-600 transition">Oefenen</Link>
                  <Link to="/#pricing" className="block text-sm text-gray-600 hover:text-blue-600 transition">Prijzen</Link>
                </nav>
              </div>

              {/* Legal */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4">Juridisch</h4>
                <nav className="space-y-2">
                  <Link to="/privacy" className="block text-sm text-gray-600 hover:text-blue-600 transition">Privacy</Link>
                  <Link to="/terms" className="block text-sm text-gray-600 hover:text-blue-600 transition">Voorwaarden</Link>
                </nav>
              </div>
            </div>

            <div className="border-t border-gray-200 mt-8 pt-8 text-center text-sm text-gray-500" suppressHydrationWarning>
              © {new Date().getFullYear()} WFT Study Buddy. Alle rechten voorbehouden.
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ArticleList;